import { firstValueFrom } from 'rxjs';
import { GlobalErrorHandler, LoggerService } from './shared/core/error-handler';
import { CountUpDirective } from './shared/directives/count-up.directive';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { NgModule, ApplicationRef, APP_INITIALIZER, ErrorHandler, Injector, inject } from '@angular/core';
import { RouterModule, PreloadAllModules, RouteReuseStrategy } from '@angular/router';

/*
 * Platform and Environment providers/directives/pipes
 */
import { ROUTES } from './app.routes';

// App is our top level component
import { AppComponent } from './app.component';
import { APP_RESOLVER_PROVIDERS } from './app.resolver';
import { DashboardComponent, DashboardSettingsComponent, DashboardService } from './dashboard';
import { ProjectModule } from './project/index';
import { ClearanceItemModule } from './clearance-item/clearance-item.module';
import { AddressBookModule } from './address-book/index'
import { AuthService } from './shared/core/index'
import { SharedModule } from './shared/shared.module';
import { AppService } from './app.service'
import { ReactiveFormsModule } from '@angular/forms';
import { ReportsModule } from './reports/reports.module'
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatCardModule } from '@angular/material/card';
import { MatSlideToggleModule, MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/slide-toggle';
import { ScrollingModule } from '@angular/cdk/scrolling';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { UserManagementModule } from './users/user-management.module';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SearchModule } from './search/search.module';

import { CustomReuseStrategy } from './shared/providers/custom-reusestrategy';

import { HttpService } from './shared/core/http.service';
import { authenticatedInterceptor } from './shared/core/auth.interceptor';
import { extendedInterceptor } from './shared/core/extended.interceptor';
import { PrizmDocViewerWrapperComponent } from './prizmdoc-viewer-wrapper';
import { DocumentService } from './docviewer/document.service';
import { DocumentDetailComponent } from './docviewer/document-detail.component';
import { NavigationTrackerService } from './shared/helper-components/navigation-tracker.service';


// Application wide providers
const APP_PROVIDERS = [
  ...APP_RESOLVER_PROVIDERS
];

type StoreType = {
  restoreInputValues: () => void,
  disposeOldHosts: () => void
};


 /**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */

 @NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent,    
    DashboardComponent,
    DashboardSettingsComponent,
    PrizmDocViewerWrapperComponent,
    DocumentDetailComponent
  ],
  /**
   * Import Angular's modules.
   */
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule.forRoot(),
    ProjectModule,
    ClearanceItemModule,
    BrowserAnimationsModule,
    AddressBookModule,
    UserManagementModule,
    ReportsModule,
    SearchModule,
    CountUpDirective,
    MatTooltipModule,
    RouterModule.forRoot(ROUTES, { useHash: true, preloadingStrategy: PreloadAllModules }),
    MatSidenavModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatListModule,
    MatDividerModule,
    MatMenuModule,
    MatCardModule,
    MatSlideToggleModule,
    ScrollingModule,
  ],
  /**
   * Expose our Services and Providers into Angular's dependency injection.
   */
  providers: [
    APP_PROVIDERS,
    AppService,
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const authService = inject(AuthService);
        return () => firstValueFrom(authService.login()).catch((err: any) => {
          console.error('startupServiceFactory error :' + err);
          authService.error = err;
          // Don't rethrow — allow app to bootstrap so user can see error page
          return false;
        });
      },
      multi: true
    },
    LoggerService,
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    { 
      provide: RouteReuseStrategy, 
      useClass: CustomReuseStrategy 
    },
    {
      provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS,
      useValue: { hideIcon: true }
    },
    HttpService,
    DocumentService,
    NavigationTrackerService,
    DashboardService,
    provideHttpClient(withInterceptors([authenticatedInterceptor, extendedInterceptor]))
  ]
})
export class AppModule {

  constructor(
    public appRef: ApplicationRef, injector: Injector) {
    AppInjector = injector;
  }

}

export let AppInjector: Injector;
